import React, { useEffect, useRef, useState } from "react";
import Button from "../../../components/common/button/Button";
import SearchableSelect from "../../../components/common/searchableSelect/SearchableSelect";
import TextField from "../../../components/common/textfield/TextField";
import { ValidateFields, ValidateNumberOrDecimal } from "../../../utils/formValidation";
import { bankAccountTypeOptions } from "../../../consts/dropDownOptions/companiesOptions";
import UploadImageInput from "../../../components/common/uploadImageInput/UploadImageInput";
import { routeNames } from "../../../consts/routesNames";
import { useLocation, useNavigate, useParams } from "react-router";
import { createCompany } from "../../../apis/companiesApis/createCompany";
import { editCompany } from "../../../apis/companiesApis/editCompany";
import "./AddEditCompanies.css";
import { useDispatch } from "react-redux";
import { openModal } from "../../../redux/modal/modalActions";
import { getCookie } from "../../../utils/cookies";
import { getAllBanks } from "../../../apis/financesApis/getAllBanks";
import { getAllContactCategories } from "../../../apis/contactsApis/getAllContactCategories";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import { PERMISSIONS } from "../../../auth/utils/permissionCodes";
import { useCrudPermissions } from "../../../auth/hooks/useCrudPermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";

function AddEditCompanies() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = getCookie("userId");
  const { canCreate, canUpdate } = useCrudPermissions(PERMISSIONS.COMPANIES);
  const { canUpdateOwnedRecord, isProjectManager } = useUpdateOwnership();

  const { id: companyId } = useParams();
  const data = location?.state?.data;
  const editable = data && Object.entries(data).length > 0;
  const canSubmitCompany = editable
    ? canUpdateOwnedRecord(data?.created_by, canUpdate)
    : canCreate;

  useEffect(() => {
    if (!editable || !isProjectManager) return;
    if (!canUpdateOwnedRecord(data?.created_by, canUpdate)) {
      dispatch(
        openModal(
          <ResponseModal
            text="You can only update companies you created."
            isError={true}
          />,
        ),
      );
      navigate(routeNames.COMPANIES);
    }
  }, [
    canUpdate,
    canUpdateOwnedRecord,
    data?.created_by,
    dispatch,
    editable,
    isProjectManager,
    navigate,
  ]);

  const [banksData, setBanksData] = useState([]);
  const [companyCategoriesData, setCompanyCategoriesData] = useState([]);

  // === Company Info States ===
  const [name, setName] = useState("");
  const [webSite, setWebSite] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [profilePicture, setProfilePicture] = useState("");

  // === Company Address States ===
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zipCode, setZipCode] = useState("");

  // === Bank Info States ===
  const [companyBank, setCompanyBank] = useState(""); // Drop Down with Get Banks
  const [companyBankAccountName, setCompanyBankAccountName] = useState("");
  const [companyBankAccountType, setCompanyBankAccountType] = useState(""); // Fixed in the BE Model and FE as Options
  const [companyBankAccountNumber, setCompanyBankAccountNumber] = useState("");
  const [companyBankRoutingNumber, setCompanyBankRoutingNumber] = useState("");

  // === Bank Address States ===
  const [bankStreetNumber, setBankStreetNumber] = useState("");
  const [bankStreetName, setBankStreetName] = useState("");
  const [bankCity, setBankCity] = useState("");
  const [bankState, setBankState] = useState("");
  const [bankCountry, setBankCountry] = useState("");
  const [bankZipCode, setBankZipCode] = useState("");

  const [comment, setComment] = useState("");
  const [category, setCategory] = useState({ id: "", name: "" }); // Drop Down with Get Categories
  const [nameError, setNameError] = useState("");
  const bankDataDic = useRef({});

  useEffect(() => {
    const getBanks = async () => {
      try {
        const allBanksRes = await getAllBanks();
        if (allBanksRes.error_code === 2 || allBanksRes.error_code === 3) {
          dispatch(openModal(<ResponseModal text={allBanksRes.error_message} isError={true} />));
        } else {
          allBanksRes?.res?.data.forEach((bank) => {
            bank["value"] = bank.name;
            bankDataDic.current[bank.name] = bank;
          });
          setBanksData(allBanksRes.res.data);
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    getBanks();
  }, [dispatch]);

  useEffect(() => {
    const getCompanyCategories = async () => {
      try {
        const allCompanyCategoriesRes = await getAllContactCategories();
        if (allCompanyCategoriesRes.error_code === 2 || allCompanyCategoriesRes.error_code === 3) {
          dispatch(openModal(<ResponseModal text={allCompanyCategoriesRes.error_message} isError={true} />));
        } else {
          setCompanyCategoriesData(allCompanyCategoriesRes.res.data);
        }
      } catch (error) {
        console.error("Error fetching company categories:", error);
      }
    };
    getCompanyCategories();
  }, [dispatch]);

  // Set All States
  useEffect(() => {
    if (editable) {
      setName(data.name);
      setWebSite(data.website);
      setCompanyEmail(data.email ?? "");

      setStreetNumber(data.street_number);
      setStreetName(data.street_name);
      setCity(data.city);
      setState(data.state);
      setCountry(data.country);
      setZipCode(data.zip_code);

      setCompanyBank(data?.company_bank?.name || "");
      setCompanyBankAccountName(data.company_bank_account_name);
      setCompanyBankAccountType(data.company_bank_account_type);
      setCompanyBankAccountNumber(data.company_bank_account_number);
      setCompanyBankRoutingNumber(data.company_bank_routing_number);

      setBankStreetNumber(data.bank_street_number);
      setBankStreetName(data.bank_street_name);
      setBankCity(data.bank_city);
      setBankState(data.bank_state);
      setBankCountry(data.bank_country);
      setBankZipCode(data.bank_zip_code);

      setComment(data.comment);
      setCategory(data.category ?? { id: "", name: "" });
    }
  }, [editable, data]);

  // === Create - Edit ReqBody ====
  const reqBody = () => {
    const baseRequestBody = {
      name: name,
      website: webSite,
      email: companyEmail.trim(),
      profile_picture: profilePicture,

      street_number: streetNumber,
      street_name: streetName,
      city: city,
      state: state,
      country: country,
      zip_code: zipCode,

      company_bank_id: companyBank === "" ? { id: "" } : bankDataDic.current[companyBank].id, // Have Logic
      company_bank_account_name: companyBankAccountName,
      company_bank_account_number: companyBankAccountNumber,
      company_bank_routing_number: companyBankRoutingNumber,
      company_bank_account_type: companyBankAccountType,
      bank_street_number: bankStreetNumber,
      bank_street_name: bankStreetName,
      bank_city: bankCity,
      bank_state: bankState,
      bank_country: bankCountry,
      bank_zip_code: bankZipCode,
      comment: comment,
      category_id: category.id,
    };
    if (editable) {
      return {
        ...baseRequestBody,
        company_id: companyId,
        updated_by_user_id: userId,
      };
    } else {
      return {
        ...baseRequestBody,
        created_by_user_id: userId,
      };
    }
  };

  // === Add Handlers ===
  const handleCompanySubmitError = (res) => {
    if (res?.field === "name") {
      setNameError(res.error_message ?? "A company with this name already exists.");
    }
    dispatch(openModal(<ResponseModal text={res?.error_message ?? "Could not save company."} isError={true} />));
  };

  const addCompanyHandler = async () => {
    if (!canSubmitCompany) return;
    setNameError("");
    const isValidated = addEditCompanyValidation();
    if (isValidated) {
      const createCompanyRes = await createCompany(reqBody());
      if (createCompanyRes.error_code === 1) {
        alert("Company Created Successfully");
        navigate(routeNames.COMPANIES);
      } else if (createCompanyRes.error_code === 3) {
        handleCompanySubmitError(createCompanyRes);
      }
    }
  };

  // === Edit Handlers ===
  const editCompanyHandler = async (e) => {
    e.preventDefault();
    if (!canSubmitCompany) return;
    setNameError("");
    const isValidated = addEditCompanyValidation();
    if (isValidated) {
      const editCompanyRes = await editCompany(reqBody());
      if (editCompanyRes.error_code === 1) {
        alert("Company Updated Successfully");
        navigate(routeNames.COMPANIES);
      } else if (editCompanyRes.error_code === 3) {
        handleCompanySubmitError(editCompanyRes);
      }
    }
  };

  // === Image Handler ===
  const handleImageChange = (e) => {
    setProfilePicture(e.target.files[0]);
  };

  // === Form Validation ===
  const addEditCompanyValidation = () => {
    if (!ValidateFields(name)) {
      alert("Name is required");
      return false;
    }
    if (streetNumber && !ValidateNumberOrDecimal(streetNumber)) {
      alert("Street Number must be a number");
      return false;
    }
    if (zipCode && !ValidateNumberOrDecimal(zipCode)) {
      alert("Zip Code must be a number");
      return false;
    }
    if (bankStreetNumber && !ValidateNumberOrDecimal(bankStreetNumber)) {
      alert("Bank Street Number must be a number");
      return false;
    }
    if (bankZipCode && !ValidateNumberOrDecimal(bankZipCode)) {
      alert("Bank Zip Code must be a number");
      return false;
    }
    if (!ValidateFields(category.id)) {
      alert("Category is required");
      return false;
    }
    return true;
  };

  return (
    <>
      <div className="main-container-addEditCompanies">
        {/* Inputs Container */}
        <div className="form-main-container-addEditCompanies">
          <div className="form-row-container-addEditCompanies">
            {/* Company Address */}
            <div className="form-column-container-addEditCompanies" id="company-address">
              <div className="form-column-addEditCompanies">
                <div className="form-card-text-container-addEditCompanies">Company Details</div>
                <div className="form-card-input-container-addEditCompanies">
                  {/* Company Address Input fields */}
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Name"}
                      name="company_name"
                      type="text"
                      setValue={(v) => {
                        setName(v);
                        if (nameError) setNameError("");
                      }}
                      value={name}
                      isRequired={true}
                      placeHolder=" Name ...."
                      maxLength={32}
                    />
                    {nameError && <p className="addEditCompanies-name-error">{nameError}</p>}
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Website"}
                      name="company_website"
                      type="text"
                      setValue={setWebSite}
                      value={webSite}
                      placeHolder=" Website ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Email"}
                      name="company_email"
                      type="email"
                      setValue={setCompanyEmail}
                      value={companyEmail}
                      placeHolder=" company@example.com"
                      maxLength={254}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Street Number"}
                      name="company_treet_number"
                      type="text"
                      setValue={setStreetNumber}
                      value={streetNumber}
                      placeHolder=" Street Number ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Street Name"}
                      name="company_street_name"
                      type="text"
                      setValue={setStreetName}
                      value={streetName}
                      placeHolder=" Street Name ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"City"}
                      name="company_city"
                      type="text"
                      setValue={setCity}
                      value={city}
                      placeHolder=" City ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"State"}
                      name="company_state"
                      type="text"
                      setValue={setState}
                      value={state}
                      placeHolder=" State ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Country"}
                      name="company_country"
                      type="text"
                      setValue={setCountry}
                      value={country}
                      placeHolder=" Country ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Zip Code"}
                      name="company_zip_code"
                      type="text"
                      setValue={setZipCode}
                      value={zipCode}
                      placeHolder=" Zip Code ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <SearchableSelect
                      onChange={(e, newValue) => {
                        setCategory(newValue);
                      }}
                      options={companyCategoriesData}
                      label={"Company Category"}
                      name="company_category"
                      value={category.name}
                      required={true}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Comment"}
                      name="comment"
                      type="text"
                      setValue={setComment}
                      value={comment}
                      placeHolder=" Comment ...."
                      maxLength={255}
                    />
                  </div>
                  {!editable && (
                    <div className="labeltext-container-addEditContacts">
                      <UploadImageInput name="Profile Picture" label="Profile Picture" onclick={(e) => handleImageChange(e)} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {false && (
          <div className="form-row-container-addEditCompanies">
            <div className="form-column-container-addEditCompanies" id="bank-info">
              <div className="form-column-addEditCompanies">
                <div className="form-card-text-container-addEditCompanies">Bank Info</div>
                <div className="form-card-input-container-addEditCompanies">
                  <div className="labeltext-container-addEditCompanies">
                    <SearchableSelect
                      label={"Bank Name"}
                      name="bank_name"
                      options={banksData}
                      onChange={(e, newValue) => {
                        setCompanyBank(e);
                      }}
                      value={companyBank}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Account Name"}
                      name="account_name"
                      type="text"
                      setValue={setCompanyBankAccountName}
                      value={companyBankAccountName}
                      placeHolder=" Account Name ...."
                      maxLength={100}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <SearchableSelect
                      label={"Bank Account Type"}
                      name="bank_account_type"
                      value={companyBankAccountType}
                      onChange={(e) => setCompanyBankAccountType(e)}
                      options={bankAccountTypeOptions}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Account Number"}
                      name="account_number"
                      type="text"
                      setValue={setCompanyBankAccountNumber}
                      value={companyBankAccountNumber}
                      placeHolder=" Account Number ...."
                      maxLength={100}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Routing Number"}
                      name="routing_number"
                      type="text"
                      setValue={setCompanyBankRoutingNumber}
                      value={companyBankRoutingNumber}
                      placeHolder=" Routing Number ...."
                      maxLength={100}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="form-column-container-addEditCompanies" id="bank-address">
              <div className="form-column-addEditCompanies">
                <div className="form-card-text-container-addEditCompanies">Bank Address</div>
                <div className="form-card-input-container-addEditCompanies">
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Street Number"}
                      name="bank_street_number"
                      type="text"
                      setValue={setBankStreetNumber}
                      value={bankStreetNumber}
                      placeHolder=" Street Number ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Street Name"}
                      name="bank_street_name"
                      type="text"
                      setValue={setBankStreetName}
                      value={bankStreetName}
                      placeHolder=" Street Name ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"City"}
                      name="bank_city"
                      type="text"
                      setValue={setBankCity}
                      value={bankCity}
                      placeHolder=" City ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"State"}
                      name="bank_state"
                      type="text"
                      setValue={setBankState}
                      value={bankState}
                      placeHolder=" State ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Country"}
                      name="bank_country"
                      type="text"
                      setValue={setBankCountry}
                      value={bankCountry}
                      placeHolder=" Country ...."
                      maxLength={32}
                    />
                  </div>
                  <div className="labeltext-container-addEditCompanies">
                    <TextField
                      label={"Zip Code"}
                      name="bankzip_code"
                      type="text"
                      setValue={setBankZipCode}
                      value={bankZipCode}
                      placeHolder=" Zip Code ...."
                      maxLength={32}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
        {/* Buttons Container */}
        <div className="button-main-container-addEditCompanies">
          <div className={"save-create-buttons-container"}>
            {editable ? (
              <div className="save-button-container">
                <Button buttonText={"Save"} onClick={(e) => editCompanyHandler(e)} disabled={!canSubmitCompany} />
              </div>
            ) : (
              <div className="create-button-container">
                <Button buttonText={"Create"} type="submit" onClick={() => addCompanyHandler()} disabled={!canSubmitCompany} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AddEditCompanies;
